"use client";

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/utils/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Trash2, Mail } from 'lucide-react';
import { showError } from '@/utils/toast';
import { showSuccess } from '@/utils/toast';

interface Invite {
  id: string;
  email: string;
  role: string;
  used: boolean;
  expires_at: string;
  created_at: string;
}

interface InviteListProps {
  companyId: string;
}

export const InviteList = ({ companyId }: InviteListProps) => {
  const [filter, setFilter] = React.useState<'all' | 'pending' | 'expired'>('all');

  const { data: invites, isLoading, refetch } = useQuery<Invite[]>({
    queryKey: ['invites', companyId, filter],
    queryFn: async () => {
      if (!companyId) return [];
      let query = supabase
        .from('invites')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (filter === 'pending') {
        query = query.eq('used', false).gte('expires_at', new Date().toISOString());
      } else if (filter === 'expired') {
        query = query.eq('used', false).lt('expires_at', new Date().toISOString());
      }

      const { data, error } = await query;
      if (error) {
        if (error.message.includes('permission')) {
          showError('Access denied. Contact superadmin.');
          return [];
        }
        throw error;
      }
      return data || [];
    },
    enabled: !!companyId,
    retry: (failureCount, error) => failureCount < 3 && !error.message.includes('permission'),
  });

  const deleteInvite = async (inviteId: string) => {
    const { error } = await supabase.from('invites').delete().eq('id', inviteId);
    if (error) {
      if (error.message.includes('permission')) {
        showError('Access denied. Contact superadmin.');
      } else {
        showError('Failed to delete invite');
      }
    } else {
      refetch();
      showSuccess('Invite deleted!');
    }
  };

  if (isLoading) {
    return <div>Loading invites...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Invites ({invites?.length || 0})</CardTitle>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => setFilter('all')}>All</Button>
          <Button variant="outline" onClick={() => setFilter('pending')}>Pending</Button>
          <Button variant="outline" onClick={() => setFilter('expired')}>Expired</Button>
        </div>
      </CardHeader>
      <CardContent>
        {invites?.length === 0 ? (
          <p className="text-muted-foreground">No invites match your filter.</p>
        ) : (
          <div className="space-y-2">
            {(invites ?? []).map((invite) => (
              <div key={invite.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1">
                  <div className="font-medium">{invite.email}</div>
                  <div className="text-sm text-muted-foreground">Role: {invite.role}</div>
                  <Badge variant={invite.used ? 'default' : 'secondary'}>
                    {invite.used ? 'Used' : 'Pending'}
                  </Badge>
                  <div className="text-xs text-muted-foreground">
                    Expires: {new Date(invite.expires_at).toLocaleDateString()}
                  </div>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteInvite(invite.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};